import { postJangjiRecommendRequest } from "@/apis/sangjoQuotation";
import { isLoggedInState } from "@/atoms/accountState";
import { matchRequestFacilityLoadingState } from "@/atoms/match/matchRequestLoadingState";
import {
  장지유형_LIST,
  장지준비긴급도_LIST,
  장지희망서비스_LIST,
} from "@/containers/MatchContainer/constants/request";
import { FacilityMatchRequestProps } from "@/containers/MatchContainer/type/requestType";
import { useDialog } from "@/hooks/useDialog";
import { Box } from "@chakra-ui/react";
import {
  cooKeys,
  customEvent,
  Dropdown,
  Field,
  FUNERAL_SIDO_LIST,
  FUNERAL_SIGUNGU,
  HStack,
  setCookie,
  Spacing,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import RequestUniqueFixedButton from "../MatchRequest/RequestUniqueFixedButton";

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({
  key: sido[1] as string,
  value: sido[0],
}));

export const 장지견적_장지핵심요소_LIST = ["관리 상태가 좋은 곳", "저렴한 가격", "모르겠어요"];

export default function MatchFacilityRequestQuotation({
  defaultFacility,
}: {
  defaultFacility: any;
}) {
  const eventPath = "match_facility_request";
  const router = useRouter();

  const [selectFuneralSigungu, setSelectFuneralSigungu] = useState(true);
  const [selectFuneralSido, setSelectFuneralSido] = useState(false);

  const isLoggedIn = useRecoilValue(isLoggedInState);

  const { onOpen, onClose } = useDialog();
  const { onOpen: onOpenDialog, onClose: onCloseDialog } = useDialog();

  const [SelectedOption, setSelectedOption] = useReducer(
    (prev: FacilityMatchRequestProps, next: any) => {
      return { ...prev, ...next };
    },
    {
      장지준비긴급도: "",
      장지유형: "",
      핵심요소: "",
      희망서비스: "",
      funeral_sido: "",
      funeral_sigungu: "",
    },
  );

  const isDisabled = useMemo(() => {
    return (
      !SelectedOption.장지준비긴급도 ||
      !SelectedOption.핵심요소 ||
      !SelectedOption.희망서비스 ||
      !SelectedOption.funeral_sigungu
    );
  }, [SelectedOption]);

  useEffect(() => {
    if (SelectedOption.funeral_sido) {
      setSelectFuneralSido(true);
      setSelectFuneralSigungu(false);
    }

    if (SelectedOption.funeral_sigungu) {
      setSelectFuneralSigungu(true);
    }
  }, [SelectedOption]);

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    customEvent({
      newGtm: {
        path: eventPath,
        trigger_and_target: "submit_conversion",
      },
    });

    if (isDisabled) {
      onOpen({
        type: 1,
        closeOnOverlayClick: false,
        header: "응답하지 않은 항목이 있어요",
        children: <>다시 확인해주세요.</>,
        footerProps: {
          text: "확인",
          onClick: onClose,
        },
      });
      return;
    }

    try {
      setLoading(true);

      const body = {
        status: 장지준비긴급도_LIST.find((item) => item.text === SelectedOption.장지준비긴급도)
          ?.value as string,
        desired_type: 장지유형_LIST.find((item) => item.text === SelectedOption.장지유형)
          ?.value as string,
        most_important_factor: SelectedOption.핵심요소,
        additional_service: 장지희망서비스_LIST.find(
          (item) => item.text === SelectedOption.희망서비스,
        )?.value as string,
        origin_sido: SelectedOption.funeral_sido,
        origin_sigungu: SelectedOption.funeral_sigungu,
        jangji_sido: defaultFacility.sido,
        jangji_sigungu: defaultFacility.sigungu,
        memo: `[개별 장지 견적 제출]: ${defaultFacility.companyname}`,
      };

      const { data, status } = await postJangjiRecommendRequest(body);
      if (status === 201) {
        customEvent({
          newGtm: {
            action: "match_request_complete",
          },
        });

        if (isLoggedIn) {
          router.push(`/match/facility-recommendation/`);
        } else {
          setCookie(cooKeys.quotation_request_facility, data);
          router.push(`/login?redirectTo=/match/facility-recommendation/`).then((res) => {
            setLoading(false);
            onClose();
          });
        }
      }
    } catch (e: any) {
      if (e.response && e.response.status === 500) {
        onOpenDialog({
          type: 1,
          closeOnOverlayClick: false,
          header: "이미 장지 견적을 받았어요",
          children: (
            <>
              작성해주신 견적서를 바탕으로 <br /> 상담이 진행중인 상태에요.
              <br /> 견적 내용을 수정하시려면,
              <br /> 카카오톡 채널로 문의주세요.
            </>
          ),
          footerProps: {
            text: "견적 확인",
            onClick: () => {
              router.push("/match/facility-recommendation/");
              onCloseDialog();
            },
          },
        });
      } else {
        console.log(e);
      }
    } finally {
      setLoading(false);
    }
  }, [SelectedOption]);

  const [loading, setLoading] = useRecoilState(matchRequestFacilityLoadingState);

  return (
    <>
      <VStack padding="24px" width="100%">
        <VStack gap="16px" position="relative">
          <Box className="subtitle2_sb gray900">
            어디에서 장례를 치를 계획이신가요?
            <br />
            <span className="body_16_m gray700">
              이장, 개장의 경우 현재 장지의 위치를 알려주세요.
            </span>
          </Box>

          <HStack gap="12px">
            <Dropdown
              name="지역"
              forceZIndex={100}
              value={SelectedOption.funeral_sido}
              values={SIDO_LIST}
              onChange={(v) => {
                setSelectedOption({ funeral_sido: v, funeral_sigungu: undefined });
                customEvent({
                  newGtm: {
                    path: eventPath,
                    trigger_and_target: "facility_funeral_sido-answer",
                    text: String(v),
                  },
                });
              }}
            />
            <Dropdown
              name="지역"
              forceZIndex={100}
              value={SelectedOption.funeral_sigungu}
              values={
                SelectedOption.funeral_sido
                  ? Array.from(FUNERAL_SIGUNGU.get(SelectedOption.funeral_sido)!).map(
                      (sigungu) => ({
                        key: sigungu[1],
                        value: sigungu[0],
                      }),
                    )
                  : []
              }
              onChange={(v) => {
                setSelectedOption({ funeral_sigungu: v });
                customEvent({
                  newGtm: {
                    path: eventPath,
                    trigger_and_target: "facility_funeral_sigungu-answer",
                    text: String(v),
                  },
                });
              }}
            />
          </HStack>
          {selectFuneralSigungu || (
            <div
              css={{ position: "absolute", bottom: -30, left: 5, zIndex: -1 }}
              className="orange650 button"
            >
              다음 지역을 선택해주세요.
            </div>
          )}
          {selectFuneralSido || (
            <div
              css={{ position: "absolute", bottom: -30, left: 5, zIndex: -1 }}
              className="orange650 button"
            >
              지역을 선택해주세요.
            </div>
          )}
        </VStack>

        <Spacing gap={60} />

        <Field.Radio
          title="가장 중요하게 생각하시는 부분이 무엇인가요?"
          items={장지견적_장지핵심요소_LIST}
          value={SelectedOption.핵심요소}
          onChange={(e) => {
            setSelectedOption({ 핵심요소: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_important_factor-answer",
                text: String(e),
              },
            });
          }}
          radioWidth="100%"
          flexDirection="column"
        />
        <Spacing gap={40} />

        <Field.Radio
          title="장지 준비가 급박한 상황인가요?"
          items={장지준비긴급도_LIST.map((item) => item.text)}
          value={SelectedOption.장지준비긴급도}
          onChange={(e) => {
            setSelectedOption({ 장지준비긴급도: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_status-answer",
                text: String(e),
              },
            });
          }}
          radioWidth="100%"
          flexDirection="column"
        />

        <Spacing gap={40} />

        <Field.Radio
          title="희망하시는 추가 서비스가 있으신가요?"
          items={장지희망서비스_LIST.map((item) => item.text)}
          value={SelectedOption.희망서비스}
          onChange={(e) => {
            setSelectedOption({ 희망서비스: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_desired_service-answer",
                text: String(e),
              },
            });
          }}
          radioWidth="100%"
          flexDirection="column"
        />

        <Spacing gap={200} />
      </VStack>

      <RequestUniqueFixedButton
        isFixed={true}
        disabled={isDisabled}
        handleSubmit={handleSubmit}
        loading={loading}
      />
    </>
  );
}
